import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { t } from "i18next";
import { classNames } from "primereact/utils";

import "./ConfirmDialog.scss";
import { ReactComponent as CloseIcon } from "../../assets/images/close-icon.svg";

const ConfirmDialog = ({
  visible,
  onHide,
  onConfirm,
  onCancel,
  seconds = 3,
  title,
  messageHeader,
  message,
  actionButtonLabel = t("confirmDialog.delete"),
  cancelButtonLabel = t("confirmDialog.cancel"),
  delayedConfirm = true,
  children = null,
  variant = undefined,
  dialogClassName = "",
}) => {
  const [countdown, setCountdown] = useState(seconds);
  const [inProgress, setInProgress] = useState(false);

  const isCounting = delayedConfirm && countdown > 0;
  const buttonSeverity = variant && variant !== "none" ? variant : undefined;

  useEffect(() => {
    if (!visible) return undefined;

    if (!delayedConfirm) {
      setCountdown(0);
      return undefined;
    }

    setCountdown(seconds);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [visible, seconds, delayedConfirm]);

  useEffect(() => {
    if (!visible) {
      setInProgress(false);
      setCountdown(seconds);
    }
  }, [visible, seconds]);

  const handleOnConfirm = async () => {
    try {
      setInProgress(true);

      if (onConfirm) {
        await onConfirm();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ConfirmDialog onConfirm error:", error);
    } finally {
      setInProgress(false);

      if (typeof onHide === "function") {
        onHide("confirm");
      }
    }
  };

  const handleOnHide = () => {
    setInProgress(false);

    if (typeof onHide === "function") {
      onHide("cancel");
    }
  };

  const handleOnCancel = () => {
    setInProgress(false);

    if (typeof onCancel === "function") {
      onCancel();
      return;
    }

    if (typeof onHide === "function") {
      onHide("cancel");
    }
  };

  return (
    <Dialog
      header={title}
      visible={visible}
      onHide={handleOnHide}
      className={classNames("confirm-dialog", dialogClassName)}
      closable
      modal
      headerClassName="confirm-dialog-header"
      focusOnShow={false}
      closeIcon={<CloseIcon />}
      appendTo={document.body}
    >
      {messageHeader && (
        <div className="confirm-dialog-description-header">
          {typeof messageHeader === "function"
            ? messageHeader()
            : messageHeader}
        </div>
      )}

      {message && (
        <div className="confirm-dialog-description">
          {message}
        </div>
      )}

      {children && (
        <div className="confirm-dialog-description">
          {children}
        </div>
      )}

      <div className={`confirm-dialog-buttons ${variant || ""}`}>
        <Button
          label={
            isCounting ? (
              <span className="counting-label">
                <i className="pi pi-spinner pi-spin" />
                {countdown}s
              </span>
            ) : (
              actionButtonLabel
            )
          }
          disabled={isCounting || inProgress}
          onClick={handleOnConfirm}
          className={classNames({
            counting: isCounting,
          })}
          severity={buttonSeverity}
          loading={inProgress}
        />

        <Button
          label={cancelButtonLabel}
          className="p-button-secondary"
          onClick={handleOnCancel}
          disabled={inProgress}
        />
      </div>
    </Dialog>
  );
};

export default ConfirmDialog;